import React from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Container, Navbar, Nav, NavDropdown } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { logout } from '../redux/slices/authSlice';

/** Derive a short brand code from a user's email domain.
 *  e.g. "alice@loop.com.na"  → "LOOP"
 *       "bob@gptf.org.na"    → "GPTF"
 *       "carol@example.com"  → "EXAMPLE"
 */
const getBrandFromEmail = (email?: string): string => {
  if (!email) return 'CMS';
  try {
    const domain = email.split('@')[1]; // e.g. "loop.com.na"
    const parts = domain.split('.');    // ["loop", "com", "na"]
    // Strip common TLD segments; take the first meaningful part
    const tldSegments = new Set(['com', 'org', 'net', 'gov', 'co', 'na', 'za', 'uk', 'io', 'app']);
    const brandPart = parts.find((p) => !tldSegments.has(p.toLowerCase()));
    return (brandPart || parts[0]).toUpperCase();
  } catch {
    return 'CMS';
  }
};

const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
  if (firstName) return firstName.slice(0, 2).toUpperCase();
  if (username) return username.slice(0, 2).toUpperCase();
  return 'U';
};

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.auth.user);

  const brandName = getBrandFromEmail(user?.email);
  const initials = getInitials(user?.first_name, user?.last_name, user?.username);
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || 'User';

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  const navLinkStyle = (path: string): React.CSSProperties => ({
    color: isActive(path) ? '#ffffff' : 'rgba(255, 255, 255, 0.65)',
    fontWeight: isActive(path) ? 600 : 400,
    fontSize: '14px',
    padding: '6px 14px',
    borderRadius: '6px',
    backgroundColor: isActive(path) ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
    transition: 'all 0.15s ease',
    textDecoration: 'none',
    marginRight: '2px',
  });

  return (
    <div className="d-flex flex-column min-vh-100" style={{ backgroundColor: '#f0f4f8' }}>
      <Navbar
        expand="lg"
        style={{
          backgroundColor: '#00579a',
          boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
          padding: '10px 0',
        }}
      >
        <Container fluid style={{ maxWidth: '1400px' }}>
          {/* Dynamic brand */}
          <Navbar.Brand
            as={Link}
            to="/dashboard"
            style={{
              color: '#ffffff',
              fontWeight: 800,
              fontSize: '20px',
              letterSpacing: '1.5px',
              textDecoration: 'none',
              marginRight: '40px',
              fontFamily: "'Sora', 'Segoe UI', sans-serif",
            }}
          >
            {brandName}
          </Navbar.Brand>

          <Navbar.Toggle aria-controls="navbar-nav" />
          <Navbar.Collapse id="navbar-nav">
            <Nav className="me-auto" style={{ gap: '2px' }}>
              {[
                { to: '/dashboard', label: 'Dashboard' },
                { to: '/bids', label: 'Bids' },
                { to: '/blogs', label: 'Blogs' },
                { to: '/events', label: 'Events' },
                { to: '/vacancies', label: 'Vacancies' },
                { to: '/bursaries', label: 'Bursaries' },
                { to: '/projects', label: 'Projects' },
                { to: '/gallery', label: 'Gallery' },
                { to: '/categories', label: 'Categories' },
              ].map(({ to, label }) => (
                <Nav.Link key={to} as={Link} to={to} style={navLinkStyle(to)}>
                  {label}
                </Nav.Link>
              ))}

              {user?.role === 'admin' && (
                <Nav.Link as={Link} to="/users" style={navLinkStyle('/users')}>
                  Users
                </Nav.Link>
              )}
            </Nav>

            {/* User dropdown */}
            <Nav>
              <NavDropdown
                align="end"
                title={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                    {/* Avatar circle */}
                    <span
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #00a8ff, #0078d2)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#fff',
                        flexShrink: 0,
                        border: '2px solid rgba(255,255,255,0.25)',
                      }}
                    >
                      {initials}
                    </span>
                    <span style={{ color: '#ffffff', fontWeight: 500, fontSize: '14px' }}>
                      {fullName}
                    </span>
                  </span>
                }
              >
                {/* User info header */}
                <div style={{ padding: '12px 20px 10px', minWidth: '220px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #00a8ff, #0078d2)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '15px',
                        fontWeight: 700,
                        color: '#fff',
                        flexShrink: 0,
                      }}
                    >
                      {initials}
                    </span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '14px', color: '#1a1a2e' }}>
                        {fullName}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '1px' }}>
                        {user?.email}
                      </div>
                    </div>
                  </div>

                  {/* Role + department badges */}
                  <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '20px',
                        background: user?.role === 'admin' ? '#fff3cd' : '#e8f4fd',
                        color: user?.role === 'admin' ? '#92660a' : '#0078d2',
                        border: `1px solid ${user?.role === 'admin' ? '#ffd97a' : '#b3d9f7'}`,
                        textTransform: 'capitalize',
                      }}
                    >
                      {user?.role || 'user'}
                    </span>
                    {user?.department && (
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 500,
                          padding: '2px 8px',
                          borderRadius: '20px',
                          background: '#f3f4f6',
                          color: '#6b7280',
                          border: '1px solid #e5e7eb',
                        }}
                      >
                        {user.department}
                      </span>
                    )}
                  </div>
                </div>

                <NavDropdown.Divider />

                {/* Job title if available */}
                {user?.job_title && (
                  <>
                    <NavDropdown.Item disabled style={{ fontSize: '13px', color: '#9ca3af', cursor: 'default' }}>
                      <span style={{ marginRight: '8px' }}>💼</span>{user.job_title}
                    </NavDropdown.Item>
                    <NavDropdown.Divider />
                  </>
                )}

                {/* Actions */}
                <NavDropdown.Item
                  as={Link}
                  to="/profile"
                  style={{ fontSize: '14px', color: '#374151', padding: '8px 20px' }}
                >
                  <span style={{ marginRight: '10px' }}>⚙️</span>Account Settings
                </NavDropdown.Item>

                <NavDropdown.Divider />

                <NavDropdown.Item
                  onClick={handleLogout}
                  style={{
                    fontSize: '14px',
                    color: '#dc2626',
                    fontWeight: 600,
                    padding: '8px 20px',
                  }}
                >
                  <span style={{ marginRight: '10px' }}>→</span>Sign out
                </NavDropdown.Item>
              </NavDropdown>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <div className="flex-grow-1">
        <Outlet />
      </div>

      <footer
        style={{
          backgroundColor: '#ffffff',
          borderTop: '1px solid #e5e7eb',
          padding: '20px 0',
        }}
      >
        <Container>
          <p style={{ textAlign: 'center', color: '#9ca3af', marginBottom: 0, fontSize: '13px' }}>
            © {new Date().getFullYear()} {brandName} CMS. All rights reserved.
          </p>
        </Container>
      </footer>
    </div>
  );
};

export default Layout;